import { useState } from 'react'
import { X, UserPlus, UserMinus, Loader2, Tag, Tags, type LucideIcon } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { cn } from '@/lib/utils'
import { useUsuariosOrg } from '@/hooks/useUsuarios'
import {
  useContarRepeticionesEtiqueta,
  useEtiquetasCliente,
} from '@/core/hooks/useEtiquetasCliente'
import type { EtiquetaCliente } from '@/core/services/etiquetasClienteService'

type Etiqueta = Pick<EtiquetaCliente, 'id' | 'nombre'>

interface ClientesBulkActionsBarProps {
  selectedCount: number
  totalCount: number
  onSelectAll: () => void
  onClearSelection: () => void
  onAsignarResponsable: (usuarioId: string) => Promise<void>
  onQuitarResponsable: () => Promise<void>
  onEtiquetar: (etiqueta: Etiqueta) => Promise<void>
  onQuitarEtiqueta: (etiqueta: Etiqueta) => Promise<void>
  /** Muestra los menús de responsables. */
  mostrarResponsables?: boolean
  /** Muestra los menús de etiquetas. */
  mostrarEtiquetas?: boolean
  isAssigning?: boolean
  className?: string
}

export function ClientesBulkActionsBar({
  selectedCount,
  totalCount,
  onSelectAll,
  onClearSelection,
  onAsignarResponsable,
  onQuitarResponsable,
  onEtiquetar,
  onQuitarEtiqueta,
  mostrarResponsables = true,
  mostrarEtiquetas = false,
  isAssigning = false,
  className,
}: ClientesBulkActionsBarProps) {
  const { data: usuarios = [] } = useUsuariosOrg({ activo: true })

  if (selectedCount === 0) return null

  const allSelected = selectedCount === totalCount && totalCount > 0

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-4 p-3 bg-accent/10 border border-accent rounded-lg animate-in slide-in-from-top-2 duration-200',
        className
      )}
    >
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={onClearSelection}
          title="Cancelar selección"
        >
          <X className="h-4 w-4" />
        </Button>

        <span className="text-sm font-medium">
          {selectedCount} {selectedCount === 1 ? 'cliente seleccionado' : 'clientes seleccionados'}
        </span>

        {!allSelected && (
          <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={onSelectAll}>
            Seleccionar todos ({totalCount})
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2">
        {mostrarEtiquetas && (
          <EtiquetasEnLote
            selectedCount={selectedCount}
            disabled={isAssigning}
            onEtiquetar={onEtiquetar}
            onQuitarEtiqueta={onQuitarEtiqueta}
          />
        )}

        {mostrarResponsables && (
          <>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={isAssigning}>
                  {isAssigning ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <UserPlus className="h-4 w-4 mr-2" />
                  )}
                  Asignar responsable
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {usuarios.map((u) => (
                  <DropdownMenuItem
                    key={u.id}
                    onClick={() => onAsignarResponsable(u.id)}
                    disabled={isAssigning}
                  >
                    {u.nombre || u.email.split('@')[0]}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              variant="outline"
              size="sm"
              onClick={onQuitarResponsable}
              disabled={isAssigning}
            >
              {isAssigning ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <UserMinus className="h-4 w-4 mr-2" />
              )}
              Quitar responsable
            </Button>
          </>
        )}
      </div>
    </div>
  )
}

/** Texto del diálogo que confirma etiquetar cuando tareas que se repiten usan la etiqueta. */
const textoConfirmarEtiquetar =(clientes: number, nombre: string, repeticiones: number) => {
  const quienes = clientes === 1 ? '1 cliente' : `${clientes} clientes`
  const uso =
    repeticiones === 1
      ? 'La usa 1 tarea que se repite: se le va a crear a cada uno.'
      : `La usan ${repeticiones} tareas que se repiten: se les van a crear a cada uno.`
  return `Vas a etiquetar a ${quienes} como "${nombre}". ${uso}`
}

interface EtiquetasEnLoteProps {
  selectedCount: number
  disabled: boolean
  onEtiquetar: (etiqueta: Etiqueta) => Promise<void>
  onQuitarEtiqueta: (etiqueta: Etiqueta) => Promise<void>
}

/**
 * Menús «Etiquetar» y «Quitar etiqueta» de la barra de selección.
 * Antes de etiquetar cuenta las tareas que se repiten con esa etiqueta, y si hay alguna pide confirmar.
 */
function EtiquetasEnLote({ selectedCount, disabled, onEtiquetar, onQuitarEtiqueta }: EtiquetasEnLoteProps) {
  const { data: catalogo = [] } = useEtiquetasCliente()
  const contarRepeticiones = useContarRepeticionesEtiqueta()
  const [confirmar, setConfirmar] = useState<{ etiqueta: Etiqueta; repeticiones: number } | null>(null)

  if (catalogo.length === 0) return null

  const elegirParaEtiquetar = async (etiqueta: Etiqueta) => {
    let repeticiones: number
    try {
      repeticiones = await contarRepeticiones(etiqueta.id)
    } catch {
      toast.error('No se pudo revisar la etiqueta. Probá de nuevo.')
      return
    }
    if (repeticiones > 0) setConfirmar({ etiqueta, repeticiones })
    else await onEtiquetar(etiqueta)
  }

  return (
    <>
      <MenuEtiquetas
        icon={Tag}
        label="Etiquetar"
        catalogo={catalogo}
        disabled={disabled}
        onElegir={elegirParaEtiquetar}
      />
      <MenuEtiquetas
        icon={Tags}
        label="Quitar etiqueta"
        catalogo={catalogo}
        disabled={disabled}
        onElegir={onQuitarEtiqueta}
      />

      <AlertDialog open={confirmar !== null} onOpenChange={(open) => !open && setConfirmar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Etiquetar clientes</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmar &&
                textoConfirmarEtiquetar(selectedCount, confirmar.etiqueta.nombre, confirmar.repeticiones)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmar) void onEtiquetar(confirmar.etiqueta)
              }}
            >
              {selectedCount === 1 ? 'Etiquetar' : `Etiquetar a los ${selectedCount}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

interface MenuEtiquetasProps {
  icon: LucideIcon
  label: string
  catalogo: Etiqueta[]
  disabled: boolean
  onElegir: (etiqueta: Etiqueta) => Promise<void>
}

function MenuEtiquetas({ icon: Icon, label, catalogo, disabled, onElegir }: MenuEtiquetasProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" disabled={disabled}>
          <Icon className="h-4 w-4 mr-2" />
          {label}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {catalogo.map((etiqueta) => (
          <DropdownMenuItem
            key={etiqueta.id}
            onClick={() => void onElegir(etiqueta)}
            disabled={disabled}
          >
            {etiqueta.nombre}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
